import { ArrowBackRounded, InboxRounded } from "@mui/icons-material";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { passwordResetRequest } from "./authFunctions";
import { fetchPasswordResetEligibility, trackPasswordResetAttempt } from "../api/fetchPasswordReset";
import LoadingButton from "../Shared/LoadingButton";
import AlertMessage from "../ui/Alert";
import { ResetPasswordErrorCodesTypes } from "./constants";
import { phraseErrorMessage } from "./helpers";
import { useCountdown } from "../../hooks/useCountdown";
import { formatSeconds } from "../../helpers";

type ResetPasswordProps = {
  setIsForgotPassword: (boolean) => void;
};

const resetPasswordTimer = 300;
const ResetPassword = ({ setIsForgotPassword }: ResetPasswordProps) => {
  const [email, setEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<ResetPasswordErrorCodesTypes | null>(null);

  const { startTimer, secondsLeft, isRunning } = useCountdown();
  useEffect(() => {
    if (!isRunning && secondsLeft < 1) {
      setErrorCode(null);
    }
  }, [isRunning, secondsLeft]);

  const handleSendInstructions = async (e) => {
    e.preventDefault();
    if (email === "") return;
    setIsLoading(true);

    try {
      const response = await fetchPasswordResetEligibility(email);
      if (response?.error_code) {
        setErrorCode(response.error_code);
        startTimer(response.retry_after_seconds);
        return;
      }
      const success = await passwordResetRequest(email);

      if (success) {
        startTimer(resetPasswordTimer);
        setIsEmailSent(true);
        trackPasswordResetAttempt(email);
      }
      else {
        setErrorCode("DEFAULT_ERROR");
      }
    }
    catch (error) {
      setErrorCode("DEFAULT_ERROR");
      console.error("Error during password reset flow:", error);
    }
    finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <div className="min-h-[308px] md:min-h-[534px] flex flex-col justify-center rounded-[10px]">
        <div className="flex flex-col items-center text-center">
          {isEmailSent
            ? (
                <div className="w-[480px] pb-8">
                  <InboxRounded className="!w-20 !h-20 md:!w-[120px] md:!h-[120px] !fill-tgpt-secondary-states-focus" />
                  <Typography variant="h4/regular-400" className="!text-[24px] md:!text-[34px] pt-2 md:pt-8">
                    Check your inbox
                  </Typography>
                  <div className="flex flex-col gap-y-4">
                    <Typography variant="body1/regular-400" className="py-4">
                      {"An email has been sent to "}
                      <strong>
                        {email}
                        {" "}
                      </strong>
                      from the TradeAlgo team.
                      {" "}
                      To reset your
                      password, click on the link in the email.
                    </Typography>

                    <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-70">
                      Be sure to check your spam folder if you can’t find the email.
                      <br></br>
                      <span>
                        Haven’t received it?
                        {" "}
                        {secondsLeft === 0
                          ? (<span className="text-primary-blue-dark cursor-pointer" onClick={() => setIsEmailSent(false)}>Send email again</span>)
                          : (
                              <span>
                                Send email again in
                                {" "}
                                <strong><u>{formatSeconds(secondsLeft)}</u></strong>
                              </span>
                            )}
                      </span>
                    </Typography>
                  </div>

                </div>
              )
            : (
                <>
                  <Typography variant="h4/regular-400" className="!text-[24px] md:!text-[34px] pb-4">
                    Reset your password
                  </Typography>
                  <Typography variant="body1/regular-400" className="text-mui-white-70">Enter the email address you registered with</Typography>
                </>
              )}
        </div>
        {!isEmailSent && (
          <>
            <form onSubmit={handleSendInstructions} className="pt-8 py-4 flex flex-col justify-start items-center gap-y-6">
              <TextField
                value={email}
                onChange={e => setEmail(e.target.value)}
                label="Email address"
                variant="outlined"
                className="w-[300px] md:w-[340px] mb-4 outlined-input labelText"
                disabled={secondsLeft > 0 || isLoading}
              />
              {errorCode && (
                <AlertMessage
                  message={phraseErrorMessage(errorCode, secondsLeft)}
                  severity="error"
                  width={340}
                />
              )}
              <LoadingButton
                isLoading={isLoading}
                className="h-[50px] text-white !text-[15px] font-medium leading-[26px] !bg-tgpt-secondary-main !normal-case disabled:!bg-action-disabled-background disabled:!text-action-disabled"
                isDisabled={!email || errorCode !== null}
                onClick={handleSendInstructions}
                fullWidth
              >
                Send instructions
              </LoadingButton>
            </form>
          </>
        )}
        <Button
          variant="text"
          className="!text-transform-none !text-tgpt-secondary-main hover:!bg-tgpt-secondary-states-hover gap-x-2 !self-center"
          onClick={() => setIsForgotPassword(false)}
        >
          <ArrowBackRounded className="!fill-tgpt-secondary-main !w-6 !h-6" />
          Back to Login
        </Button>
      </div>
    </Box>
  );
};

export default ResetPassword;
